/**
 * Created by dadwic on 3/3/2019.
 */

import axios, { AxiosInstance, CancelTokenSource } from "axios";
import md5 from "md5";
import * as R from "ramda";
import { readToken } from "helpers/token";
import { bzUuid, sortKeys } from "helpers/index";
import showNotify from "helpers/notify";
import useUser from "hooks/useUser";
import AppStore from "state/index";

/**
 * This class for make api services
 */
abstract class BaseApiService {
	/**
	 * Private property Entity name
	 * @type {string}
	 */
	entity = "";
	/**
	 * Private property resource axios instance
	 * @type {AxiosInstance}
	 */
	private resource!: AxiosInstance;
	/**
	 * Private property section between {shop|panel}
	 * @type {string}
	 */
	section = "";
	/**
	 * This property make cancel token and we could cancel request
	 * @type {CancelTokenSource}
	 */
	source!: CancelTokenSource;
	/**
	 * baseUrl of api
	 * @type {string}
	 */
	baseURL = process.env.REACT_APP_BASE_URL;

	prefix = process.env.REACT_APP_BASE_PREFIX_URL;

	/**
	 * api version
	 * @type {string}
	 */
	_version = "v1";

	/**
	 * search filter
	 * @type {Array}
	 */
	filters: Array<any> = [];
	/**
	 * search order by
	 * @type {Array}
	 */
	orderBy: Array<any> = [];

	_page = 1;

	_size = 10;

	_authToken: any;

	_searchPath = "search";

	/**
	 * Constructor for get basic params
	 * @param entity
	 * @param config
	 */
	constructor(entity: string, config?: any) {
		this.entity = entity;
		this.makeResource(config);
	}

	/**
	 * make "axios resource"
	 */
	makeResource = (config?: any) => {
		const CancelToken = axios.CancelToken;
		this.source = CancelToken.source();
		const configs = {
			...config,
			// /api/panel/account/v1/activate
			baseURL: `${this.baseURL}${this.prefix}/${this.entity}/${this.version}`,
			cancelToken: this.source.token,
			withCredentials: true,
			headers: {
				"Content-Type": "application/json",
			},
		};

		this.resource = axios.create(configs);

		this.requestInterceptors();
		this.responseInterceptors();
	};

	/**
	 * make dynamic "axios resource"
	 * @param entity
	 * @param version
	 */
	dynamicMakeResource = (entity: string = this.entity, version: string = this.version) => {
		const CancelToken = axios.CancelToken;
		this.version = version;
		this.source = CancelToken.source();
		const configs = {
			// /api/panel/account/v1/activate
			baseURL: `${this.baseURL}${this.prefix}/${entity}/${version}`,
			cancelToken: this.source.token,
			withCredentials: true,
			headers: {
				"Content-Type": "application/json",
			},
		};

		const dResource = axios.create(configs);

		this.requestInterceptors(dResource);
		this.responseInterceptors(dResource);

		return dResource;
	};

	private responseInterceptors(resource = this.resource) {
		// Intercept responses before they are handled by then or catch.
		resource.interceptors.response.use(
			response => R.pathOr(response, ["data", "data"])(response),
			error => {
				const token = readToken() || AppStore.userStore.token;
				const statusCode = R.path(["response", "status"])(error);
				if (statusCode === 401 && token) {
					const { logout } = useUser();
					logout();
				} else if (statusCode === 502) {
					showNotify({
						text: "سرور در حال به روز رسانی است.",
					});
				} else {
					const responseError = R.path(["response", "data", "error", "detail"])(error) as string;
					showNotify({
						text: responseError || "خطایی در سرور رخ داده است",
					});
				}

				return Promise.reject(error);
			},
		);
	}

	private requestInterceptors(resource = this.resource) {
		resource.interceptors.request.use(
			config => {
				if (!config.headers.Authorization) {
					const token = readToken() || AppStore.userStore.token;

					if (token) {
						config.headers.Authorization = `Bearer ${token}`;
					}
				}

				if (this.version === "v3") {
					config.headers["request-id"] = bzUuid();
					config.headers["request-time"] = new Date().getTime();
					const headers = sortKeys(config.headers);
					let hMac = "";
					Object.keys(headers).forEach((key: string) => {
						if (typeof headers[key] === "string") {
							hMac += `${headers[key]};`;
						}
					});
					console.log(hMac);
					config.headers["X-Authorization-Content-MD5"] = md5(hMac);
				}

				return config;
			},
			error => Promise.reject(error),
		);
	}

	/**
	 * Get Resource
	 * @return {AxiosInstance}
	 */
	getResource(): AxiosInstance {
		return this.resource;
	}

	/**
	 * Search api call
	 * @param {object} filters
	 * @param {object} config
	 * @return {Promise}
	 */
	search(filters?: SearchServices.SearchDto, config: object = {}): HttpService.SearchResponse<any> {
		return this.resource.post(`/${this.searchPath}`, filters, config);
	}

	/**
	 * Load Api
	 * @param {string} id
	 * @param {object} config
	 * @return {Promise}
	 */
	load(id: any, config: object = {}): Promise<any> {
		return this.resource.get(`/load/${id}`, config);
	}

	/**
	 * Create Api
	 * @param data
	 * @param config
	 * @return {Promise}
	 */
	create(data: any, config = {}): Promise<any> {
		return this.resource.post("/create", data, config);
	}

	/**
	 * Update Api
	 * @param data
	 * @param config
	 * @return {Promise}
	 */
	update(data: any, config = {}): Promise<any> {
		return this.resource.post("/update", data, config);
	}

	/**
	 * Delete Api
	 * @param id
	 * @param config
	 * @return {Promise}
	 */
	delete(id: any, config = {}): Promise<any> {
		return this.resource.get(`/delete/${id}`, config);
	}

	/**
	 * cancel request function
	 */
	cancel() {
		// cancel the request (the message parameter is optional)
		return this.source.cancel("Operation canceled by the user.");
	}

	/**
	 * make filter array
	 * @param {string} field
	 * @param {string} operator
	 * @param value
	 * @param {undefined | boolean} or
	 * @return {BaseApiService}
	 */
	makeFilters(field: string, operator: { toUpperCase: () => void }, value: any, or: any = false): BaseApiService {
		if (value) {
			this.filters.push({
				field,
				operator: operator.toUpperCase(),
				value,
				or: !!or,
			});
		}

		return this;
	}

	/**
	 * clear filter array
	 */
	clearFilter() {
		this.filters = [];
		return this;
	}

	/**
	 * clear order array
	 */
	clearOrder() {
		this.orderBy = [];
		return this;
	}

	/**
	 * make order by array
	 * @param {string} name
	 * @param {boolean} asc
	 * @return {BaseApiService}
	 */
	makeOrderBy(name: any, asc: any): BaseApiService {
		this.orderBy.push({
			name,
			asc,
		});

		return this;
	}

	/**
	 * get search filter object
	 * @return {{size: BaseApiService._size, orderBy: BaseApiService.orderBy, filters: BaseApiService.filters, page: BaseApiService._page}}
	 */
	getSearchFilter() {
		const { filters, page, orderBy, size } = this;

		return {
			filters,
			orderBy,
			page,
			size,
		};
	}

	get version() {
		return this._version;
	}

	set version(value) {
		this._version = value;
	}

	get authToken() {
		return this._authToken;
	}

	set authToken(value) {
		this._authToken = value;
	}

	get page() {
		return this._page;
	}

	set page(value) {
		this._page = value;
	}

	get size() {
		return this._size;
	}

	set size(value) {
		this._size = value;
	}

	get searchPath() {
		return this._searchPath;
	}

	set searchPath(value) {
		this._searchPath = value;
	}
}

// @ts-ignore
export default BaseApiService;
